import type { CallResult, Gender } from '../api/client.ts';

const WEEKDAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/**
 * Genitive, because a month only ever appears here after a day: «4 августа».
 *
 * Hardcoded rather than asked of `Intl`, which the client uses in exactly one
 * place and nowhere near this file.
 */
const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local midnight of whatever day this instant falls in. */
export function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** `10:05` — the label under every bubble. */
export function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * `0:12`, `1:05` — how long a recording runs, under a кружочек and beside a
 * waveform. Seconds are rounded rather than truncated, so a 7.6-second note
 * does not read as 0:07 while its own progress bar is already at the end.
 */
export function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.round(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`;
}

/**
 * The right-hand label in a chat-list row, as in the design: today shows a
 * clock, yesterday says so, this week names the day, older shows a date.
 */
export function formatListTimestamp(timestamp: number, now = Date.now()): string {
  const today = startOfDay(new Date(now));
  const then = startOfDay(new Date(timestamp));

  if (then === today) return formatTime(timestamp);
  if (then === today - DAY_MS) return 'вчера';
  if (then > today - 7 * DAY_MS) return WEEKDAYS[new Date(timestamp).getDay()]!;

  const date = new Date(timestamp);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
}

/** Whether two instants fall on the same calendar day, locally. */
export function isSameDay(left: number, right: number): boolean {
  return startOfDay(new Date(left)) === startOfDay(new Date(right));
}

/**
 * The label on the chip that stands above the first message of each day.
 *
 * The boundary is local midnight rather than a rolling 24 hours, so something
 * written at 23:59 reads as «Вчера» both at half past midnight and the next
 * afternoon — the same rule the chat list follows. The year is only spelled out
 * when it is not the current one; inside a conversation being read now, «2026»
 * on every chip would be noise.
 *
 * Capitalised, unlike the list's «вчера», because there it is the tail of a
 * sentence and here it is the whole of one.
 */
export function formatDaySeparator(timestamp: number, now = Date.now()): string {
  const today = startOfDay(new Date(now));
  const then = startOfDay(new Date(timestamp));

  if (then === today) return 'Сегодня';
  if (then === today - DAY_MS) return 'Вчера';

  const date = new Date(timestamp);
  const label = `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  return date.getFullYear() === new Date(now).getFullYear()
    ? label
    : `${label} ${date.getFullYear()}`;
}

/**
 * How long until the next local midnight — the one moment a day separator
 * changes wording on its own, and so the only moment worth waking up for.
 *
 * Built from the local date constructor rather than by adding 24 hours, which
 * would land an hour out on either side of a daylight-saving change. The floor
 * is there because a call made exactly at midnight would otherwise ask for a
 * zero-length timer, and a timer that fires immediately re-arms immediately.
 */
export function msUntilNextDay(now = Date.now()): number {
  const date = new Date(now);
  const midnight = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime();
  return Math.max(1000, midnight - now);
}

/**
 * Picks the right Russian form for a count: 1 минуту, 2 минуты, 5 минут.
 * Teens are the exception the modulo has to be checked against first.
 */
function plural(count: number, one: string, few: string, many: string): string {
  const tens = count % 100;
  if (tens >= 11 && tens <= 14) return many;
  const ones = count % 10;
  if (ones === 1) return one;
  if (ones >= 2 && ones <= 4) return few;
  return many;
}

/**
 * The tail of «был(а) в сети …» in the conversation header: how long ago while
 * that is still a number a person can feel, then a clock, then a date.
 *
 * The point where it switches from «40 минут назад» to «в 10:05» is an hour,
 * because past that nobody counts in minutes; the day boundary rather than a
 * rolling 24 hours decides «вчера», so a Tuesday morning is never called
 * yesterday on Wednesday afternoon.
 */
export function formatLastSeen(timestamp: number, now = Date.now()): string {
  const elapsed = now - timestamp;
  const minute = 60 * 1000;

  // A clock skewed forward, or a stamp written between render and tick.
  if (elapsed < minute) return 'только что';
  if (elapsed < 60 * minute) {
    const minutes = Math.floor(elapsed / minute);
    return `${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')} назад`;
  }

  const today = startOfDay(new Date(now));
  const then = startOfDay(new Date(timestamp));

  if (then === today) return `в ${formatTime(timestamp)}`;
  if (then === today - DAY_MS) return `вчера в ${formatTime(timestamp)}`;
  return `${formatListTimestamp(timestamp, now)} в ${formatTime(timestamp)}`;
}

/**
 * The line under a person's name: in the conversation header, and in their
 * profile. One function because the two are the same sentence about the same
 * person, and two copies of it would drift the first time one was reworded.
 *
 * Someone who has never connected — a fresh account the other side started a
 * chat with — has no time to show, so it falls back to the plain «не в сети»
 * this used to say for everyone.
 *
 * The ending comes from `users.gender`, which is set per account and never
 * guessed from a name: «был(а)» is what this line said about everybody before
 * that column existed and is still what it says about anybody the app has not
 * been told about, which is the only honest answer to a question nobody asked.
 *
 * «в сети» is in the online half and deliberately not in the other one. The
 * header has a name, a status and the buttons on one line, and «был(а) в сети
 * вчера в 22:15» was long enough to cut the name short on a phone — while the
 * two words it loses are the two nobody reads: «был вчера в 22:15» says the
 * same thing about the same person.
 *
 * Takes the three facts rather than a ChatSummary, so that it stays as testable
 * as everything else here and a caller holding a peer from somewhere other than
 * the chat list can still use it.
 */
export function presenceLabel(
  online: boolean,
  lastSeenAt: number | null,
  gender: Gender | null,
  now = Date.now(),
): string {
  if (online) return 'в сети';
  if (lastSeenAt === null) return 'не в сети';
  return `${wasHere(gender)} ${formatLastSeen(lastSeenAt, now)}`;
}

/** «был» / «была» / «был(а)» — the only word in the app that an ending changes. */
function wasHere(gender: Gender | null): string {
  if (gender === 'm') return 'был';
  if (gender === 'f') return 'была';
  return 'был(а)';
}

/**
 * The line under «Пароль» in the settings list: how long the password on the
 * account has been the one it is.
 *
 * Coarse on purpose, and that is why formatLastSeen could not be reused: past
 * yesterday that one turns into a date — «4 августа в 10:05» — which is the
 * right answer for «when was this person here» and the wrong one for a fact
 * nobody counts in hours. What this row is for is telling somebody at a glance
 * that a password has been sitting there since spring.
 *
 * `null` is an account whose password predates the column, so the row says what
 * the screen does instead of inventing a date.
 */
export function formatPasswordAge(changedAt: number | null, now = Date.now()): string {
  if (changedAt === null) return 'Изменить пароль';

  const days = Math.floor((startOfDay(new Date(now)) - startOfDay(new Date(changedAt))) / DAY_MS);
  if (days <= 0) return 'изменён сегодня';
  if (days === 1) return 'изменён вчера';
  if (days < 30) return `изменён ${days} ${plural(days, 'день', 'дня', 'дней')} назад`;

  const months = Math.floor(days / 30);
  if (months < 12) return `изменён ${months} ${plural(months, 'месяц', 'месяца', 'месяцев')} назад`;
  return 'изменён больше года назад';
}

/** `340 КБ`, `2.4 МБ` — matching the file-message and composer-chip labels. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} КБ`;
  const mb = kb / 1024;
  // A single attachment is capped well below this, but the admin totals are not.
  if (mb >= 1024) {
    const gb = mb / 1024;
    return gb < 10 ? `${gb.toFixed(1)} ГБ` : `${Math.round(gb)} ГБ`;
  }
  // One decimal below 10 MB (2.4 МБ), whole numbers above — same as the design.
  return mb < 10 ? `${mb.toFixed(1)} МБ` : `${Math.round(mb)} МБ`;
}

/** «3 участника» — the group header's status line and its info screen's subtitle. */
export function formatMembers(count: number): string {
  return `${count} ${plural(count, 'участник', 'участника', 'участников')}`;
}

/** «Доступен 3 пользователям» — the line under «Сохранить» on the bots tab. */
export function formatBotAudience(count: number): string {
  return `Доступен ${count} ${plural(count, 'пользователю', 'пользователям', 'пользователям')}`;
}

/**
 * «2 БОТА» — the tag on a folder's row in the chat list.
 *
 * Upper case in the string rather than in the CSS: it is the tag's whole text,
 * and a `text-transform` would hide the fact that the plural has to agree with
 * a number that is being shouted.
 */
export function formatBotCount(count: number): string {
  return `${count} ${plural(count, 'БОТ', 'БОТА', 'БОТОВ')}`;
}

/**
 * What a group of photos and videos is called in one line — «3 фото», «2 видео»,
 * «5 вложений» — with one of either named for what it is, since «1 фото» in a
 * chat list reads like a count of something nobody was counting.
 *
 * Mirrors albumLabel in server/src/chats.ts, which writes the same line for the
 * chat list and for push; this copy is what an optimistic snippet uses before
 * the server has said anything. Neither «фото» nor «видео» declines, which is
 * why only the mixed case needs a plural at all.
 */
export function formatAlbumLabel(photos: number, videos: number): string {
  const total = photos + videos;
  if (total === 1) return photos === 1 ? 'Изображение' : 'Видео';
  if (videos === 0) return `${total} фото`;
  if (photos === 0) return `${total} видео`;
  return `${total} ${plural(total, 'вложение', 'вложения', 'вложений')}`;
}

/** «2 файла», «5 файлов» — how many of a pick the server would refuse. */
export function formatFileCount(count: number): string {
  return `${count} ${plural(count, 'файл', 'файла', 'файлов')}`;
}

/** The heading over the search results: «4 сообщения». */
export function formatSearchCount(count: number): string {
  return `${count} ${plural(count, 'сообщение', 'сообщения', 'сообщений')}`;
}

/** «Выделено 24 символа» — what the selection bar calls what it is holding. */
export function formatPickedCount(count: number): string {
  return `Выделено ${count} ${plural(count, 'символ', 'символа', 'символов')}`;
}

/** «N вложений» — the compose screen's title, which counts everything as one thing. */
export function formatAttachmentCount(count: number): string {
  return `${count} ${plural(count, 'вложение', 'вложения', 'вложений')}`;
}

/**
 * What a bubble still on its way says it is doing: «Сжимаем 42%», «Отправляем
 * 42%».
 *
 * Two verbs rather than one bar, because they are two different waits and only
 * one of them is the network. A picked video is re-encoded on this device before
 * anything is uploaded (see lib/videoCompress.ts) — a few seconds during which
 * nothing at all is going out, and a bar labelled «отправляем» would be lying
 * about where the time is going. Naming the phase also explains the size that
 * arrives at the other end.
 *
 * Both the кружочек overlay and the album bubble read from here, so the two
 * cannot drift apart.
 */
export function formatSendingLabel(phase: 'compressing' | 'uploading', percent: number): string {
  const done = Math.min(100, Math.max(0, Math.round(percent)));
  return `${phase === 'compressing' ? 'Сжимаем' : 'Отправляем'} ${done}%`;
}

/**
 * How a finished call reads in one line — the chat-list snippet, and the second
 * line of the bubble in the thread.
 *
 * Only a call that connected says which way it went. «Исходящий» on a call the
 * reader missed would be describing the caller's experience back to them, so
 * the three that never connected name what happened instead — the same sentence
 * on both sides of the conversation.
 *
 * Mirrors callPreview in server/src/chats.ts, which writes the same line for the
 * chat list and for push.
 */
export function formatCall(call: {
  fromMe: boolean;
  callResult: CallResult | null;
  callVideo: boolean | null;
  durationMs: number | null;
}): string {
  if (call.callResult === 'missed') return 'Пропущенный звонок';
  if (call.callResult === 'declined') return 'Звонок отклонён';
  if (call.callResult === 'failed') return 'Не удалось дозвониться';
  const noun = call.callVideo ? 'видеозвонок' : 'звонок';
  const label = `${call.fromMe ? 'Исходящий' : 'Входящий'} ${noun}`;
  return call.durationMs ? `${label} · ${formatDuration(call.durationMs)}` : label;
}

/**
 * Micro-dollars as «$12.34». Mirrors formatMicros in server/src/ai-budget.ts.
 *
 * Truncated rather than rounded, in the one direction that matters: a figure
 * overstating the bill is the one somebody gets shouted at over. A hundredth of
 * a cent is therefore «$0.00», which is the honest thing to say about a text
 * turn that cost one.
 */
export function formatMoney(micros: number): string {
  const cents = Math.floor(Math.max(0, micros) / 10_000);
  return `$${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}
